import * as React from 'react';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { detectTextDirection } from '../../utils/textUtils';
import './CrossCheckClauseCard.css';

export type PlaybookRecordPreviewData = {
  xml?: string | null;
  plainText?: string | null;
  side?: 'our_side' | 'their_side' | null;
  loading?: boolean;
  error?: boolean;
};

/** Rich clause preview — same markup/classes as Past negotiations options in CrossCheckClauseCard. */
export const PlaybookRecordClausePreview: React.FC<{
  data: PlaybookRecordPreviewData;
}> = ({ data }) => {
  const html = React.useMemo(() => {
    const xml = data.xml?.trim();
    if (!xml) return null;
    try {
      return TrackChangesParser.convertOoxmlToHtml(xml);
    } catch {
      return null;
    }
  }, [data.xml]);

  const plainText = (data.plainText ?? '').trim();

  const dir = React.useMemo((): 'rtl' | 'ltr' => {
    if (data.xml) {
      try {
        return detectTextDirection(TrackChangesParser.extractText(data.xml!));
      } catch {
        /* fall through */
      }
    }
    return detectTextDirection(plainText);
  }, [data.xml, plainText]);

  if (data.loading && !html && !plainText) {
    return <p className="cc-section-text cc-empty">Loading clause…</p>;
  }

  if (html) {
    return (
      <div
        className={`cc-paragraph-xml-display${data.side ? ` cc-side-${data.side}` : ''}`}
        dir={dir}
      >
        <div
          className="cc-selected-text-xml"
          dangerouslySetInnerHTML={{ __html: html }}
          dir={dir}
        />
      </div>
    );
  }

  if (plainText) {
    return (
      <p className="cc-section-text" dir={dir}>
        {plainText}
      </p>
    );
  }

  if (data.error) {
    return <p className="cc-section-text cc-empty">Could not load clause preview.</p>;
  }

  return <p className="cc-section-text cc-empty">No clause text available.</p>;
};
